import clsx from 'clsx'
import ActionsMenu, { ActionsMenuItem } from 'components/ActionsMenu/ActionsMenu'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import { ReactComponent as ZoneSvg } from 'images/icons/cleaning_zone_small.svg'
import { ReactComponent as RemoveSvg } from 'images/icons/delete.svg'
import { ReactComponent as ZoneRestrSvg } from 'images/icons/draw_zone_restricted.svg'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { MissionActions } from 'store/actions/mission'
import { selectHighlightedFeatureIdInList } from 'store/selectors/mission'
import { selectMissionInfo } from 'store/selectors/mission'
import store from 'store/store'
import { missionThunks } from 'store/thunks/mission'
import { MISSION_ZONE_TYPES } from 'utils/constants'
import { isComboOrOmniRobotType } from 'utils/robotTypeDisplayName'
import getTestId from 'utils/getTestId'
import { IRestrictZone } from 'utils/interfaces'

import style from './ZoneItem.module.css'

const RestrictedZoneItem = ({
  zone,
  number,
  hideActions,
}: {
  zone: IRestrictZone
  number: number
  hideActions?: boolean
}) => {
  const itemRef = useRef<HTMLDivElement>(null)
  const [showConfirmDelete, setShowConfirmDelete] = useState(false)
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const highlightedFeatureIdInList = useSelector(selectHighlightedFeatureIdInList)
  const missionInfo = useSelector(selectMissionInfo)
  const hideCleaningType = missionInfo ? !isComboOrOmniRobotType(missionInfo.robot_type) : false
  const highlighted = useMemo(() => {
    return highlightedFeatureIdInList === zone.properties.zone_id || highlightedFeatureIdInList === zone.id
  }, [highlightedFeatureIdInList, zone])

  useEffect(() => {
    if (highlighted && itemRef.current) {
      itemRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [highlighted])

  const zoneItemStyle = hideCleaningType ? style.zoneItemShort : style.zoneItem

  const onZoneClicked = () => {
    dispatch(MissionActions.setHighlightedFeatureInList(zone.properties.zone_id))
    dispatch(MissionActions.setHighlightedFeature(zone))
  }

  const onEditZoneClicked = () => {
    store.dispatch(missionThunks.setEditingZone(zone))
  }

  const onRemoveZoneClicked = () => {
    setShowConfirmDelete(true)
  }

  const handleDeleteConfirm = () => {
    if (zone.properties?.zone_id) {
      store.dispatch(missionThunks.deleteRestrictZone(zone))
    }
    setShowConfirmDelete(false)
  }

  return (
    <div
      ref={itemRef}
      className={highlighted ? clsx(zoneItemStyle, style.highlighted) : zoneItemStyle}
      onClick={onZoneClicked}
    >
      <span></span>
      <div className={style.zoneItemColSmall}></div>
      <div className={style.zoneItemColSmall}>
        {zone.properties?.zone_type === MISSION_ZONE_TYPES.RESTRICT ? (
          <ZoneRestrSvg className={style.icon} />
        ) : (
          <ZoneSvg className={style.icon} />
        )}
      </div>
      <div className={style.zoneItemCol}>{zone.properties?.name}</div>
      {!hideCleaningType && <div className={style.zoneItemCol}></div>}
      <div className={style.zoneItemCol} style={{ gridColumn: 'span 2' }}></div>
      <div className={style.zoneItemColSmall}>
        {!hideActions && (
          <ActionsMenu>
            <ActionsMenuItem onClick={onEditZoneClicked} data-test-id={getTestId('edit-zone-list')}>
              <span className={style.menuItemIcon} />
              <span data-test-id={getTestId('edit-title')}>{t('common.edit')}</span>
            </ActionsMenuItem>
            <ActionsMenuItem onClick={onRemoveZoneClicked} data-test-id={getTestId('remove-zone-list')}>
              <RemoveSvg className={style.menuItemIcon} />
              <span data-test-id={getTestId('delete-title')}>{t('common.delete')}</span>
            </ActionsMenuItem>
          </ActionsMenu>
        )}
      </div>
      <ConfirmDialog
        isOpen={showConfirmDelete}
        onClose={() => setShowConfirmDelete(false)}
        onConfirmClick={handleDeleteConfirm}
        title={t('mission.delete_restricted_zone_confirm', { name: zone.properties?.name })}
        confirmText={t('common.delete')}
      />
    </div>
  )
}

export default RestrictedZoneItem
